import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import BaseUrl from "../../base-api";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import moment from "moment/moment";
import jsPDF from "jspdf";
import { submitPaymentForm } from "../../Utils/Commons";
import logo from "./../../assets/logo.jpg";
import hkcl from "../../assets/hkcl.png";
import imageCompression from "browser-image-compression";
import spinner from "./../../assets/images/Spinner.gif";
import { toast } from "react-toastify";
import { Button } from "reactstrap";
import "jspdf-autotable";

const AdmissionList = () => {
  const [applications, setApplications] = useState([]);
  const navigate = useNavigate();
  const [statusType, setStatusType] = useState("Total");
  const [title, setTitle] = useState("All Admissions");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(10);
  const [isLoading, setIsLoading] = useState(false);
  const [payment, setPayment] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  // const location = useLocation();
  // useEffect(() => {
  //     const { status, title } = location.state || {};
  //     if (status) {
  //         setStatusType(status);
  //         setTitle(title);
  //         console.log("title" + title)
  //     } else {
  //         console.warn('Status not found in location state, using default status.');
  //     }
  // }, [location.state]);

  useEffect(() => {
    const fetchApplications = async () => {
      if (!statusType) {
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${BaseUrl}/admin/fetchApplicationstatus`,
          {
            headers: {
              Authorization: localStorage.getItem("$3ingT0k3n"),
            },
            params: {
              statusType: statusType,
              page: currentPage,
              size: pageSize,
            },
          }
        );

        setApplications(response.data.content);
        setTotalPages(response.data.totalPages);
      } catch (error) {
        setError("Failed to fetch data");
      } finally {
        setLoading(false);
      }
    };

    fetchApplications();
  }, [statusType, currentPage, pageSize]);

  const handlePageChange = (direction) => {
    setCurrentPage((prevPage) => {
      if (direction === "next" && prevPage < totalPages - 1) {
        return prevPage + 1;
      } else if (direction === "prev" && prevPage > 0) {
        return prevPage - 1;
      }
      return prevPage;
    });
  };

  const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
  };

  if (loading)
    return (
      <div className="col-xl-12 col-xxl-12 col-sm-12">
        <div className="card me-2">
          <div className="card-header text-center">
            <img src={spinner} alt="Loading..." width={150} />

            <p>Please wait ....</p>
          </div>
        </div>
      </div>
    );
  if (error) return <p>{error}</p>;

  const exportData = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Registered Candidates");

    // Define worksheet columns
    worksheet.columns = [
      { header: "Candidate Name", key: "candidateName" },
      { header: "Father Name", key: "fatherName" },
      { header: "Date of Birth", key: "dob" },
      { header: "Mobile Number", key: "mobileNumber" },
      { header: "Email", key: "email" },
      { header: "Address", key: "address" },
      { header: "Course", key: "course" },
      { header: "Enrolled Date", key: "enrolled" },
      { header: "Fee Paid", key: "feePaid" },
      { header: "Photograph", key: "photo" },
      { header: "Signature", key: "sign" },
      { header: "12th Document", key: "12th" },
      { header: "University Document (if any)", key: "uni" },
    ];

    // Loop through each data entry
    for (const t of applications) {
      // Add a row with data
      const row = worksheet.addRow({
        candidateName: t.applicantName,
        fatherName: t.fatherName,
        dob: t.dob,
        mobileNumber: t.mobileNumber,
        email: t.email,
        address: t.address,
        course: t.course,
        enrolled: t.enrolledOn,
        feePaid: t.status === "COURSE_FEE_PAID" ? "Yes" : "No",
      });

      // Starting column index for images
      let nextImageCol = 6;
      // Loop through each document and add images
      for (const [docIndex, doc] of t.documents.entries()) {
        try {
          // Convert base64 string to buffer
          const imageBuffer = Buffer.from(doc.data, "base64");

          // Determine the image extension
          const mimeType = getMimeTypeFromByteArray(imageBuffer);
          const extension = mimeType === "image/jpeg" ? "jpeg" : "png";

          // Add image to the workbook
          const imageId = workbook.addImage({
            buffer: imageBuffer,
            extension: extension,
          });

          // Add the image to the specific cell
          worksheet.addImage(imageId, {
            tl: { col: nextImageCol, row: row.number - 1 }, // Top-left corner of the cell
            ext: { width: 100, height: 100 }, // Dimensions of the image
          });

          // Move to the next column for the next image
          nextImageCol++;
        } catch (error) {
          console.error("Error adding image:", error);
        }
      }
    }

    try {
      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      saveAs(blob, "CourseFeePaid.xlsx");
    } catch (error) {
      console.error("Error writing Excel file:", error);
    }
  };
  const getMimeTypeFromByteArray = (buffer) => {
    const arr = new Uint8Array(buffer).subarray(0, 4);
    let header = "";
    for (let i = 0; i < arr.length; i++) {
      header += arr[i].toString(16);
    }

    switch (header) {
      case "89504e47":
        return "image/png";
      case "ffd8ffe0":
      case "ffd8ffe1":
      case "ffd8ffe2":
      case "ffd8ffe3":
      case "ffd8ffe8":
        return "image/jpeg";
      default:
        return "application/octet-stream";
    }
  };
  const compressImage = async (base64Image) => {
    const response = await fetch(base64Image);
    const blob = await response.blob();
    const options = {
      maxSizeMB: 2,
      maxWidthOrHeight: 1200,
      useWebWorker: true,
    };
    try {
      const compressedBlob = await imageCompression(blob, options);
      const compressedBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.readAsDataURL(compressedBlob);
        reader.onloadend = () => resolve(reader.result);
      });

      return compressedBase64;
    } catch (error) {
      console.error("Error during image compression", error);
      return base64Image;
    }
  };
  const downloadDetails = async (course) => {
    try {
      let photo = null;
      let sign = null;
      for (const data of course.documents) {
        if (data.data) {
          const base64Image = "data:image/jpeg;base64," + data.data;
          const compressedImageBase64 = await compressImage(base64Image);
          switch (data.docName) {
            case "photo":
              photo = compressedImageBase64;
              break;
            case "sign":
              sign = compressedImageBase64;
              break;
            default:
              break;
          }
        }
      }
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const currentTime = new Date().toLocaleString();

      const websiteName =
        "Guru Jambheshwar University of Science & Technology, Hisar";
      const websiteNameWork = "Admission Form";
      const websitelogName = "and";
      const websiteHkcl = "Haryana Knowledge Corporation Limited (HKCL)";
      // const websiteNameWork = "Registration Form";

      const imageWidth = 20;
      const imageHeight = 20;
      const imageX = 10;

      doc.setFont("helvetica");

      const addHeader = () => {
        doc.setFontSize(8).setTextColor("black");
        const generatedTextWidth = doc.getTextWidth(
          `Generated on: ${currentTime}`
        );
        const generatedX = pageWidth - generatedTextWidth - 10;
        doc.text(`Generated on: ${currentTime}`, generatedX, 10);
        doc.text("https://gjuskillcourses.hkcl.in", 10, 10);
      };

      doc.setFontSize(16).setFont("helvetica", "bold");
      addHeader();
      doc.addImage(logo, "JPEG", imageX, 12, imageWidth, imageHeight);

      doc.setFontSize(12).setTextColor("green");

      doc.text(websiteName, imageX + imageWidth + 12, 20);
      doc.text(websitelogName, imageX + imageWidth + 70, 25);
      doc.text(websiteHkcl, imageX + imageWidth + 30, 30);

      const websiteNameWorkWidth = doc.getTextWidth(websiteNameWork);
      const centerX = (pageWidth - websiteNameWorkWidth) / 2;
      const textY = 37;

      doc.text(websiteNameWork, centerX - 4, textY);

      const textHeight = 1.5;
      doc.line(
        centerX - 4,
        textY + textHeight,
        centerX - 3 + websiteNameWorkWidth,
        textY + textHeight
      );
      doc.addImage(
        hkcl,
        "JPEG",
        pageWidth - imageX - imageWidth - 5,
        14,
        imageWidth + 5,
        imageHeight
      );

      doc.setFontSize(12).setTextColor("green");
      doc.text("Personal Details", 15, 40);
      doc.autoTable({
        startY: 44,
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: 100 },
        },
        body: [
          [
            { content: "Candidate Name", styles: { fontStyle: "bold" } },
            course.applicantName,
          ],
          [
            { content: "Father Name", styles: { fontStyle: "bold" } },
            course.fatherName,
          ],
          [
            { content: "Mobile Number", styles: { fontStyle: "bold" } },
            course.mobileNumber,
          ],
          [{ content: "Email", styles: { fontStyle: "bold" } }, course.email],
          [
            { content: "Date of Birth", styles: { fontStyle: "bold" } },
            moment(course.dob).format("DD/MM/YYYY"),
          ],
          [
            { content: "Full Address", styles: { fontStyle: "bold" } },
            `${course.address}, ${course.district}, ${course.state}, ${course.country}, ${course.pinCode}`,
          ],
        ],
        theme: "grid",
        styles: {
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          fontSize: 10,
        },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            addHeader();
          }

          const x = 160;
          const y = 44;
          doc.addImage(photo, "JPEG", x, y, 35, 35);
        },
      });

      doc.setFontSize(12).setTextColor("green");
      doc.text("Course Details", 15, doc.autoTable.previous.finalY + 10);
      doc.setFontSize(16);

      // Prepare bulleted course text
      let enrolledCourseText = course.course;
      if (course.childCourses && course.childCourses.length > 0) {
        enrolledCourseText += ":\n" + course.childCourses.map(c => `- ${c}`).join("\n");
      }

      doc.autoTable({
        startY: doc.autoTable.previous.finalY + 12,
        body: [
          [
            { content: "Enrolled Course", styles: { fontStyle: "bold" } },
            enrolledCourseText,
          ],
        ],
        theme: "grid",
        styles: {
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          cellPadding: 3,
          fontSize: 10,
        },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            addHeader();
          }
        },
        // Wrap text inside the cell
        columnStyles: {
          1: { cellWidth: 130 }, // adjust width to wrap bullet list
        },
      });


      doc.setFontSize(12).setTextColor("green");
      doc.text(
        "Details of 12th Standard or Equivalent",
        15,
        doc.autoTable.previous.finalY + 10
      );
      doc.setFontSize(16);
      doc.autoTable({
        startY: doc.autoTable.previous.finalY + 15,
        body: [
          [
            { content: "Roll Number", styles: { fontStyle: "bold" } },
            course.rollNumber,
          ],
          [{ content: "Board", styles: { fontStyle: "bold" } }, course.board],
          [
            { content: "Passing Month-Year", styles: { fontStyle: "bold" } },
            course.passingYear,
          ],
          [
            { content: "Marks Obtained", styles: { fontStyle: "bold" } },
            course.marks,
          ],
        ],
        theme: "grid",
        styles: {
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          fontSize: 10,
        },
        didDrawPage: (data) => {
          if (data.pageNumber > 1) {
            addHeader();
          }
        },
      });
      doc.setFontSize(12).setTextColor("green");
      doc.text("Submitted Documents", 15, doc.autoTable.previous.finalY + 10);
      doc.autoTable({
        startY: doc.autoTable.previous.finalY + 15,
        head: [["Sr. No.", "Name"]],
        body: course.documents.map((item, index) => [index + 1, item.docName]),
        theme: "grid",
        styles: {
          lineWidth: 0.1,
          lineColor: [0, 0, 0],
          halign: "center",
          fontSize: 10,
        },
        columnStyles: {
          0: { cellWidth: 40 },
        },
        didDrawPage: (data) => {
          // Add header on new pages
          if (data.pageNumber > 1) {
            addHeader();
          }
        },
      });
      const paragraphText =
        "I declare that the information provided by me in the application form is true and correct to the best of my knowledge, and I have not concealed any facts. I also confirm that I have carefully read all the guidelines and instructions provided on the admission portal before submitting my application. I also acknowledge the importance of providing correct contact details, as all course-related updates will be sent to my registered mobile number and email ID. I am fully aware that the course once selected cannot be changed under any circumstances and that the course fee, once paid, is non-refundable and non-transferable. I also give my consent to receive updates and course-related information on my registered mobile number/WhatsApp and email ID.";

      const textStartY = doc.autoTable.previous.finalY + 10;
      const signatureStartY = textStartY + 25;

      doc.setFontSize(8);

      const lines = doc.splitTextToSize(paragraphText, 175);

      doc.text(lines, 15, textStartY, { maxWidth: 180, align: "justify" });

      doc
        .setFontSize(8)
        .setFont("helvetica", "underline")
        .setTextColor("black");

      const marginBottom = 10;
      const signatureTextHeight = 10;
      const signatureImageHeight = 10;

      const signatureTextY = doc.internal.pageSize.height - marginBottom;
      const signatureImageY = signatureTextY - signatureImageHeight - 5;

      doc.addImage(
        sign,
        "JPEG",
        pageWidth - 50,
        signatureImageY,
        35,
        signatureImageHeight
      );

      doc.text("(Applicant Signature)", pageWidth - 40, signatureTextY);
      doc.save("Admission Form.pdf");
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };
  const payRegFee = async (mobileNumber) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/payRegFee?mobileNumber=${mobileNumber}`,
        {},
        {
          headers: {
            Authorization: localStorage.getItem("$3ingT0k3n"),
          },
        }
      );
      if (response.status == 200) {
        toast.success(response.data.message, {
          className: "toast-large",
          position: "top-center",
        });
        setLoadingMessage("Waiting for payment confirmation....");
        setPayment(true);
        submitPaymentForm(response.data);
      } else toast.error(response.data.message, { position: "top-center" });
    } catch (error) {
      toast.error(error.response.data.message, { position: "top-center" });
    }
  };
  const payCourseFee = async (mobileNumber) => {
    try {
      const response = await axios.post(
        `${BaseUrl}/payCourseFee?mobileNumber=${mobileNumber}`,
        {},
        {
          headers: {
            Authorization: localStorage.getItem("$3ingT0k3n"),
          },
        }
      );
      if (response.status == 200) {
        toast.success(response.data.message, {
          className: "toast-large",
          position: "top-center",
        });
        setLoadingMessage("Waiting for payment confirmation....");
        setPayment(true);
        submitPaymentForm(response.data);
      } else toast.error(response.data.message, { position: "top-center" });
    } catch (error) {
      toast.error(error.response.data.message, { position: "top-center" });
    }
  };
  return (
    <div className="table-container">
      <div
        style={{
          maxWidth: "800px",
          margin: "0 auto",
          textAlign: "justify",
          padding: "20px",
          backgroundColor: "#f9f9f9",
          boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
          borderRadius: "10px",
        }}
      >
        <p
          style={{
            textAlign: "center",
            fontSize: "1rem",
            fontWeight: "bold",
            color: "#004085",
          }}
        >
          Welcome to Our Marketing Channel Partner Portal!
        </p>
        <p>We are glad to have you on board.</p>

        <p>
          To register a student for any course, please click on the{" "}
          <strong>"New Admission"</strong> option available on the portal.
        </p>

        <p>
          <strong>Student Registration Process:</strong>
        </p>
        <ol style={{ paddingLeft: "20px" }}>
          <li>
            Fill Admission Form – Enter the student’s basic and course details.
          </li>
          <li>
            Pay Registration Fee – Submit the Registration Fee Amount to
            proceed.
          </li>
          <li>
            Upload & Approve Documents – Ensure all required documents are
            uploaded and verified.
          </li>
          <li>
            Pay Course Fee – Complete the course fee payment to confirm
            admission.
          </li>
        </ol>

        <p>For any assistance, feel free to contact our support team.</p>

        <p style={{ textAlign: "right", fontStyle: "italic" }}>
          — Team CDOE GJU - HKCL
        </p>
      </div>
      <div className="container-fluid my-3">
  <div className="row align-items-center">
    <div className="col-12 col-md-8 text-center text-md-start mb-2 mb-md-0">
      <h2 className="m-0" style={{ color: "#000dd3" }}>
        {title}
      </h2>
    </div>
    <div className="col-12 col-md-4 text-center text-md-end">
      <Button color="primary" onClick={() => navigate("/Sidebar/new-admission")}>
        New Admission
      </Button>
    </div>
  </div>
</div>


      <div className="table-responsive">
        <table className="table table-bordered w-100">
          <thead>
            <tr>
              <th>Sr. No.</th>
              <th>Learner Name</th>
              <th>User ID</th>
              <th>Reg Fee Status</th>
              <th>Course Fee Status</th>
              <th>Admission Form</th>
            </tr>
          </thead>
          <tbody>
            {applications.map((app, index) => (
              <tr key={index}>
                <td>{index + 1 + currentPage * pageSize}</td>
                <td>
                  {app.applicantName}
                  <br />
                  <b>S/D/o</b> {app.fatherName}
                  <br />
                  <b>Contact: </b>
                  {app.mobileNumber}
                </td>
                <td>
                  <b>Candidate Code: </b>
                  {app.candidateCode}
                  <br />
                  <b>Learner Code: </b>
                  {app.learnerCode || "N/A"}
                </td>
                <td>
                  {app.regFeeAllowed === true ? (
                    <Button
                      className="btn-sm btn-success"
                      onClick={() => payRegFee(app.mobileNumber)}
                    >
                      Pay Fee
                    </Button>
                  ) : (
                    "Fee Paid"
                  )}
                </td>
                <td>
                  {app.courseFeeAllowed === true ? (
                    <Button
                      className="btn-sm btn-success"
                      onClick={() => payCourseFee(app.mobileNumber)}
                    >
                      Pay Fee
                    </Button>
                  ) : app.courseFeeAllowed === false ? (
                    "Fee Paid"
                  ) : (
                    "N/A"
                  )}
                </td>
                <td>
                  {app.courseFeeAllowed === false ? (
                    <a
                      style={{ color: "blue" }}
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        downloadDetails(app);
                      }}
                    >
                      Print
                    </a>
                  ) : (
                    "N/A"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="pagination-controls">
        <button
          onClick={() => handlePageChange("prev")}
          disabled={currentPage === 0}
        >
          Previous
        </button>
        <span>
          Page {currentPage + 1} of {totalPages}
        </span>
        <button
          onClick={() => handlePageChange("next")}
          disabled={currentPage >= totalPages - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default AdmissionList;
